import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { toastLoading, toastSuccess, toastWarning } from "@/utils/toast";
import { uploadImage } from "@/api/upload";
import { getStaffConfig, submitSuggestion } from "@/api/staff";
import { useWorkspaceStore } from "@/store/useWorkspaceStore";

const BRAND = "#FF6B35";

type ImgItem = { url: string };

export default function WorkspaceRequestionPage() {
  const navigate = useNavigate();
  const config = useWorkspaceStore((s) => s.config);
  const setConfig = useWorkspaceStore((s) => s.setConfig);

  const [typeId, setTypeId] = useState<number>(0);
  const [typeName, setTypeName] = useState<string>("");
  const [content, setContent] = useState<string>("");
  const [imgs, setImgs] = useState<ImgItem[]>([]);
  const [submitting, setSubmitting] = useState(false);

  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (config) return;
    (async () => {
      try {
        const c = await getStaffConfig();
        setConfig(c);
      } catch {
        // request 层统一 toast
      }
    })();
  }, [config, setConfig]);

  const suggestTypes = useMemo(
    () => config?.staff_suggest_type ?? [],
    [config],
  );

  const canAddMore = imgs.length < 4;

  const handleChooseType = (row: { id: number; name: string }) => {
    setTypeId(row.id);
    setTypeName(row.name);
  };

  const handlePickImage = () => {
    if (!canAddMore) return toastWarning("最多上传 4 张图片");
    fileRef.current?.click();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const h = toastLoading("上传中...");
      const url = await uploadImage(file);
      h.close();
      setImgs((prev) => [...prev, { url }].slice(0, 4));
    } catch {
      // uploadImage 内已 toastError
    } finally {
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const handleSubmit = async () => {
    const trimmed = content.trim();
    if (!typeName) return toastWarning("请选择您要反馈的类型");
    if (!trimmed) return toastWarning("请描述您的反馈内容");

    setSubmitting(true);
    const h = toastLoading("提交中...");
    try {
      await submitSuggestion({
        type: typeName,
        content: trimmed,
        imgs: imgs.length ? imgs.map((i) => i.url) : undefined,
      });
      toastSuccess("提交成功，感谢您的反馈");
      setTypeId(0);
      setTypeName("");
      setContent("");
      setImgs([]);
      setTimeout(() => navigate(-1), 800);
    } catch (err: any) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.msg ||
        err?.message ||
        "";
      toastWarning(msg || "提交失败");
    } finally {
      h.close();
      setSubmitting(false);
    }
  };

  return (
    <div
      className="min-h-screen"
      style={{
        background: "#F7F7F7",
        fontFamily: "'SF Pro Text','PingFang SC','Inter',system-ui,sans-serif",
      }}
    >
      {/* Header */}
      <div
        className="bg-white px-5 flex items-center gap-3"
        style={{
          paddingTop: "calc(env(safe-area-inset-top,0px) + 16px)",
          paddingBottom: 13,
          borderBottom: "1px solid #F5F5F5",
        }}
      >
        <button
          onClick={() => navigate(-1)}
          className="w-9 h-9 flex items-center justify-center rounded-full cursor-pointer active:scale-[0.98] transition-transform shrink-0"
          style={{ background: "#F3F4F6" }}
        >
          <i
            className="ri-arrow-left-line"
            style={{ fontSize: 20, color: "#374151" }}
          />
        </button>
        <span className="text-lg font-bold text-gray-800">问题反馈</span>
      </div>

      <div className="px-4 pt-4 pb-24 space-y-3">
        <div className="bg-white rounded-2xl p-4">
          {/* 问题类型 */}
          <div className="pb-4 border-b border-gray-100">
            <div className="text-base font-bold text-gray-900 mb-3">
              问题类型
            </div>
            <div className="space-y-3">
              {suggestTypes.map((t) => {
                const active = typeId === t.id;
                return (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => handleChooseType(t)}
                    className="w-full flex items-center gap-3 py-2.5 px-3 rounded-xl active:bg-orange-50 transition-colors"
                  >
                    <span
                      className={`w-[18px] h-[18px] rounded-full flex items-center justify-center border ${active ? "" : ""}`}
                      style={{
                        borderColor: active ? BRAND : "#D1D5DB",
                        background: active ? BRAND : "#fff",
                      }}
                    >
                      {active && (
                        <i className="ri-check-line text-white text-xs" />
                      )}
                    </span>
                    <span className="flex-1 text-sm text-gray-800 text-left truncate">
                      {t.name}
                    </span>
                  </button>
                );
              })}
              {suggestTypes.length === 0 && (
                <div className="text-xs text-gray-400">暂无问题类型配置</div>
              )}
            </div>
          </div>

          {/* 反馈内容 */}
          <div className="pt-4 mb-4">
            <div className="text-base font-bold text-gray-900 mb-3">
              反馈内容
            </div>
            <div className="p-3 rounded-2xl" style={{ background: "#F3F4F6" }}>
              <textarea
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder="请输入您的意见内容"
                className="w-full text-sm outline-none bg-transparent resize-none"
                style={{ minHeight: 96 }}
              />
            </div>
          </div>

          {/* 反馈图片 */}
          <div>
            <div className="text-base font-bold text-gray-900 mb-3">
              反馈图片
            </div>
            <div className="flex flex-wrap gap-2">
              {imgs.map((img, idx) => (
                <div
                  key={idx}
                  className="relative w-[76px] h-[76px] rounded-2xl overflow-hidden bg-gray-100"
                >
                  <img
                    src={img.url}
                    alt=""
                    className="w-full h-full object-cover"
                    onClick={() => window.open(img.url, "_blank")}
                  />
                  <button
                    type="button"
                    onClick={() =>
                      setImgs((prev) => prev.filter((_, i) => i !== idx))
                    }
                    className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-white shadow flex items-center justify-center active:scale-[0.98]"
                    aria-label="删除图片"
                  >
                    <i className="ri-close-line text-gray-600" />
                  </button>
                </div>
              ))}

              {canAddMore && (
                <button
                  type="button"
                  onClick={handlePickImage}
                  className="w-[76px] h-[76px] rounded-2xl flex items-center justify-center"
                  style={{ background: "#F3F4F6" }}
                >
                  <i className="ri-add-line text-2xl text-gray-400" />
                </button>
              )}
            </div>
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handleFileChange}
            />
            <div className="text-xs text-gray-400 mt-2">最多上传 4 张</div>
          </div>
        </div>
      </div>

      {/* 底部提交按钮 */}
      <div
        className="fixed inset-x-0 bottom-0 p-4 bg-[#F7F7F7] box-border z-50"
        style={{ width: "100%", maxWidth: "100vw", paddingBottom: "calc(1rem + env(safe-area-inset-bottom, 0px))" }}
      >
        <button
          onClick={handleSubmit}
          disabled={submitting}
          className={`w-full py-3.5 rounded-2xl text-sm font-semibold text-white cursor-pointer active:scale-[0.98] transition-all ${submitting ? "opacity-60" : ""}`}
          style={{ background: BRAND }}
        >
          {submitting ? "提交中..." : "提交"}
        </button>
      </div>
    </div>
  );
}
